import BaseService from './BaseService'
import * as errors from '../errors'
import _ from 'lodash'
import { decorator as reusePromise } from 'reuse-promise'
import _uniq from 'lodash-bound/uniq'
import { EscalationChangeAction, GroupType } from '../models/enums'
import { arrayWrap, jsonCloneDeep } from '../utils'
import { isEmail } from '../utils/email'
import { isPhone } from '../utils/phone'

export default class GroupsService extends BaseService {
  mounted() {
    this._fetched = false
    this._groupMemberIds = {}
    this._userGroups = {}
    this._userState = {}
    this.host.models.Conversation.on('afterInject', this._onChangeConversation)
    this.host.models.Conversation.on('afterEject', this._onRemoveConversation)
    this.host.models.Group.on('afterInject', this._onChangeGroup)
    this.host.models.Group.on('afterEject', this._onRemoveGroup)
    this.host.models.Metadata.on('afterInject', this._onChangeMetadata)
    this.host.models.Metadata.on('afterEject', this._onChangeMetadata)
    this.host.models.User.on('afterInject', this._onChangeUser)
    this.host.models.User.on('afterEject', this._onRemoveUser)
  }

  dispose() {
    this._fetched = false
    this._groupMemberIds = {}
    this._userGroups = {}
    this._userState = {}
    this.host.models.Conversation.removeListener('afterInject', this._onChangeConversation)
    this.host.models.Conversation.removeListener('afterEject', this._onRemoveConversation)
    this.host.models.Group.removeListener('afterInject', this._onChangeGroup)
    this.host.models.Group.removeListener('afterEject', this._onRemoveGroup)
    this.host.models.Metadata.removeListener('afterInject', this._onChangeMetadata)
    this.host.models.Metadata.removeListener('afterEject', this._onChangeMetadata)
    this.host.models.User.removeListener('afterInject', this._onChangeUser)
    this.host.models.User.removeListener('afterEject', this._onRemoveUser)
  }

  /**
   * Create a group
   * @param  {string} options.name - Group name
   * @param  {File} options.avatarFile - Avatar
   * @param  {Boolean} options.replayHistory - Replay History
   * @param  {Array<string>} options.memberIds - Member IDs
   * @param  {string} options.organizationId - Organization ID
   * @param  {Object} options.metadata - Optional metadata
   * @param  {string} options.senderId - ID of role or user who is creating the group
   * @return {Promise.<Group,Error>} - A promise with a group
   */
  async create({
    avatarFile,
    description,
    isPatientGroup = false,
    memberIds = [],
    metadata,
    name,
    organizationId,
    replayHistory = false,
    senderId = this.host.currentUserId,
  }) {
    this.host.requireUser()
    if (!organizationId) throw new errors.ValidationError('organizationId', 'required')

    const users =
      (await Promise.all([senderId, ...memberIds]
        .map(this.host.roles.__resolveRoleId)
        .filter(Boolean)
        .map(userId => this._resolveModelIdWithTypes(userId, 'user'))
        .map(userId => this.host.users.findOrCreate(userId, { organizationId }))
      ))
      :: _uniq()

    memberIds = users.map(userId => this._resolveModelIdWithTypes(userId, 'user'))
    const excludeCreator = this._shouldExcludeSender(organizationId, memberIds, senderId)

    if (!name && !isPatientGroup) {
      name = this._getGroupName(users)
    }

    const response = await this.host.api.groups.create({
      avatarFile,
      description,
      excludeCreator,
      memberIds,
      name,
      organizationId,
      replayHistory,
    })

    let group
    if (this.config.condensedReplays && response.entity) {
      group = this.host.conversations.__injectCounterParty({
        conversationId: response.conversation_id,
        entityAttrs: response.entity,
        entityType: 'group',
        organizationId: response.organization_id,
      })
      this.host.conversations.__injectConversation({ ...response, entity: group })
    } else {
      group = this.host.models.Group.inject(response)
    }

    if (metadata) {
      await this.host.metadata.update(group.id, metadata, organizationId)
    }

    return group
  }

  createPatientGroup({
    metadata,
    name,
    organizationId,
    recipientId,
    senderId,
  }) {
    return this.create({
      isPatientGroup: true,
      metadata,
      name,
      organizationId,
      memberIds: [senderId, ...recipientId],
    })
  }

  _shouldExcludeSender(organizationId, memberIds, senderId) {
    const sender = this.host.models.User.get(senderId)
    const isUser = item => item && item.$entityType === 'user'
    const isRole = item => isUser(item) && item.isRoleBot

    if (isRole(sender)) {
      if (sender.botRole && sender.botRole.members.length > 0) {
        return !memberIds.includes(sender.botRole.memberIds[0])
      }
    }

    return false
  }

  _getGroupName(users) {
    return users
      .map(user => {
        const { displayName, firstName } = user
        if (user.isRoleBot) return displayName
        if (firstName) return firstName
        if (isEmail(displayName) || isPhone(displayName)) return displayName
        return ''
      })
      .filter(Boolean)
      .join(', ') || 'Group'
  }

  /**
   * Update a group
   * @param  {Group} group - a group object with updated properties
   * @param  {string} group.id - Group ID (required)
   * @return {Promise.<Group,Error>} - a promise with the updated group
   */
  async update(id: string | Object, {
    name,
    avatarFile, // TODO
    replayHistory,
    memberIds,
    metadata
  }) {
    this.host.requireUser()
    id = this._resolveModelId(id)

    // TODO validation

    const newAttrs = {}
    if (typeof name !== 'undefined') newAttrs.name = name
    if (typeof replayHistory !== 'undefined') newAttrs.replayHistory = replayHistory
    if (typeof memberIds !== 'undefined') {
      newAttrs.memberIds = memberIds.map(this.host.roles.__resolveRoleId)
    }

    const res = await this.host.api.groups.update(id, {
      name,
      avatarFile,
      replayHistory,
      memberIds,
    })

    if (typeof res.avatarUrl !== 'undefined') newAttrs.avatarUrl = res.avatarUrl

    const group = this.host.models.Group.inject({ id, ...newAttrs })

    if (metadata) {
      await this.host.metadata.update(id, metadata, group.organizationId)
    }

    return group
  }

  @reusePromise()
  /**
   * Find a group
   * @param  {string} id - group ID
   * @param  {Boolean} options.bypassCache - Force fetch from server
   * @param  {Boolean} options.ignoreNotFound - Ignore if not found - promise will return null
   * @return {Promise.<Group,Error>} - a promise with the group, or error
   */
  async find(id: string, options = {}) {
    options = _.defaults(options, {
      bypassCache: false,
      ignoreNotFound: false,
      includeMembers: false,
    })
    this.host.requireUser()

    let group = null

    // local first
    if (!options.bypassCache) {
      group = this.host.models.Group.get(id)
      if (group && !group.$placeholder) {
        await this.__ensureEntitiesLoaded(group.id, options)
        return group
      }
    }

    const data = await this.__find(id)
    group = this.host.models.Group.get(id)

    if (!data) {
      if (!options.ignoreNotFound) {
        throw new errors.NotFoundError(this.host.models.Group.name, id)
      }
      return group
    }

    if (group) {
      await this.__ensureEntitiesLoaded(group.id, options)
    }

    return group
  }

  @reusePromise()
  async __find(id: string) {
    let group = this.host.models.Group.get(id)

    const data = await this.host.api.groups.find(id)
    if (this.config.condensedReplays && data && data.entity) {
      const { entity: entityAttrs } = data
      group = this.host.conversations.__injectCounterParty({
        conversationId: data.conversation_id,
        entityAttrs,
        entityType: 'group',
        organizationId: data.organization_id,
      })

      this.host.models.Group.removePlaceholder({ entity: group, attrs: entityAttrs })

      return entityAttrs
    } else {
      if (data) {
        this.__removeForumMembers(data)
        const { metadata } = data

        group = this.host.models.Group.inject(data)

        const { organizationId } = group
        if (metadata && organizationId) this.host.metadata.__injectMetadata(id, organizationId, metadata)
      }

      this.host.models.Group.removePlaceholder({ entity: group, attrs: data })

      return data
    }
  }

  __removeForumMembers(data) {
    const { members: memberIds } = data
    const groupType = this.__extractGroupTypeFromAttrs(data)
    if (groupType === GroupType.FORUM) {
      if (memberIds && memberIds.length === 0) {
        // the server always returns `members: []` for forums; represent those as null (unfetched) instead
        delete data['members']
      }
    } else if (groupType === GroupType.ROLE_ASSIGNMENT) {
      // ignore this kind of group
    } else {
      if (!memberIds) data['members'] = []
    }
  }

  __extractGroupTypeFromAttrs(attrs) {
    const id = attrs.id || attrs['token']
    const organizationId = attrs.organizationId || attrs['organization_key']
    const metadataEntity = organizationId ? this.host.metadata.get(id, organizationId) : null
    const metadata = attrs.metadata || (metadataEntity ? metadataEntity.data : null)
    let groupType = GroupType.GROUP

    if (metadata && metadata['meta_type'] === 'role_assignment') {
      groupType = GroupType.ROLE_ASSIGNMENT
    } else if (metadata && metadata['feature_service'] === 'role') {
      groupType = GroupType.ROLE_P2P
    } else if (metadata && metadata['feature_service'] === 'escalation') {
      groupType = GroupType.ESCALATION
    } else if (metadata && metadata['feature_service'] === 'patient_care') {
      groupType = GroupType.PATIENT_CARE
    } else if (metadata && metadata['feature_service'] === 'patient_messaging') {
      groupType = GroupType.PATIENT_MESSAGING
    } else if (attrs.isPublic || attrs['is_public']) {
      groupType = GroupType.FORUM
    }

    return groupType
  }

  @reusePromise()
  async __ensureEntitiesLoaded(id: string, options) {
    const group = this.host.models.Group.get(id)

    const promises = [this.ensureMetadata(group)]

    if (options.includeMembers) {
      promises.push(this.ensureMembers(group))
    }

    await Promise.all(promises)
  }

  @reusePromise()
  async findAll({ includeMembers = false } = {}) {
    if (this._fetched) return this.getAll()

    let organizations = this.host.models.Organization.getAll()
    if (organizations.length === 0) {
      organizations = await this.host.organizations.findAll()
    }

    const { results } = await this.host.search.query({
      types: ['group'],
      organizationIds: organizations.map(o => o.id),
      query: { displayName: '' },
      followContinuations: true,
    })
    const groups = results.map(searchResult => searchResult.entity)

    if (includeMembers) {
      const byOrgId = {}
      for (const { organizationId, memberIds } of groups) {
        for (const memberId of memberIds) {
          if (!byOrgId[organizationId]) byOrgId[organizationId] = []
          if (!byOrgId[organizationId].includes(memberId)) {
            byOrgId[organizationId].push(memberId)
          }
        }
      }

      let extraPromises = []
      for (const [organizationId, memberIds] of Object.entries(byOrgId)) {
        extraPromises.push(this.host.users.ensureUsers(memberIds, organizationId))
      }

      await Promise.all(extraPromises)
    }

    this._fetched = true

    return groups
  }

  @reusePromise()
  async findAllWithSpecificMembers(
    memberIds: string | string[] | Object[],
    organizationId: string | Object,
    options: ?Object,
  ) {
    organizationId = this._resolveModelId(organizationId)
    memberIds = arrayWrap(memberIds).map(this._resolveModelId).map(this.host.roles.__resolveRoleId)

    let groups = this.__findAllLocalWithSpecificMembers(memberIds, organizationId, options)

    if (groups.length === 0) {
      await this.findAll()
      groups = this.__findAllLocalWithSpecificMembers(memberIds, organizationId, options)
    }

    return groups
  }

  /**
   * Ensures all members in group are loaded, and if not, fetches them from the server
   * @param  {Group} group
   */
  async ensureMembers(group: Object, options = {}) {
    if (!group.memberIds) {
      if (group.groupType === GroupType.FORUM) {
        const memberIds = await this.findAllMemberIds(group.id)
        if (memberIds) {
          this.host.models.Group.inject({ id: group.id, members: memberIds })
        }
      } else if (group.groupType === GroupType.ROLE_ASSIGNMENT) {
        // ignore this kind of group
      } else {
        await this.__find(group.id)
      }
    }

    const attrs = options.attrs ? { ...options.attrs } : {}
    attrs.organizationId = group.organizationId

    await this.ensureMembersWithIds(group.id, group.memberIds, { ...options, attrs })
  }

  async ensureMembersWithIds(id: string, memberIds: string[], options = {}) {
    this.host.requireUser()

    memberIds = _.without(memberIds, this.host.currentUserId)
    if (memberIds.length === 0) return

    options = _.defaults(options, { onlyPlaceholder: false })

    const ensuredMembers = await Promise.all(
      memberIds.map(memberId => this.__ensureMember(memberId, options)).filter(Boolean)
    )

    if (ensuredMembers.length > 0) this.host.models.Group.touch(id)
  }

  __ensureMember(memberId: string, options = {}) {
    if (!this.host.models.User.shouldEnsure(memberId, options.onlyPlaceholder)) return null

    const user = this.host.models.User.get(memberId)
    if (user && options.attrs) {
      this.host.models.User.injectPlaceholder({ id: user.id, ...options.attrs })
    }
    return this.host.models.User.waitForEnsuredEntity(memberId, options)
  }

  async ensureMetadata(group: Object) {
    const { groupType, id, organizationId } = group
    const metadataEntity = organizationId ? this.host.metadata.get(id, organizationId) : null

    if (!metadataEntity) {
      if (groupType === GroupType.FORUM || groupType === GroupType.ROLE_ASSIGNMENT) {
        // ignore these kinds of groups
      } else {
        await this.__find(id)
      }
    }
  }

  getAll() { return this.host.models.Group.getAll() }
  getById(id: string) { return this.host.models.Group.get(id) }
  getMultiById(ids: Array<string>) { return this.host.models.Group.getMulti(ids) }

  // TODO make a property group.memberIdsString - contains sorted IDs, easier compare
  __findAllLocalWithSpecificMembers(memberIds: string[], organizationId: string, {
    exact = true,
    includeCurrentUser = true,
    includeForums = true,
    includeRoles = true,
    senderId = null,
  } = {}) {
    const { currentUser, currentUserId } = this.host
    const myBotUserIds = currentUser.roles.map(({ botUserId }) => botUserId)
    if (senderId && myBotUserIds.includes(senderId)) {
      memberIds = memberIds.slice()
      memberIds.push(senderId, currentUserId)
    } else if (includeCurrentUser && !memberIds.includes(currentUserId)) {
      memberIds = memberIds.slice()
      memberIds.push(currentUserId)
    }

    const matchesCurrentUser = memberIds.includes(currentUserId)
    const memberCnt = memberIds.length
    const all = this.host.models.Group.getAll()
    const groups = []

    for (const group of all) {
      if (!includeForums && group.groupType === GroupType.FORUM) continue

      const { memberIds: groupMemberIds, organizationId: groupOrgId } = group
      if (groupOrgId !== organizationId || !groupMemberIds) continue

      let hasAllMembers = true
      for (const memberId of memberIds) {
        if (!groupMemberIds.includes(memberId)) {
          hasAllMembers = false
          break
        }
      }
      if (!hasAllMembers) continue

      if (exact) {
        if (!senderId && !matchesCurrentUser) continue

        let matchCount = memberCnt
        if (includeRoles) {
          for (const botUserId of myBotUserIds) {
            if (group.memberIds.includes(botUserId)) {
              matchCount++
            }
          }
        }

        if (matchCount !== group.memberIds.length) continue
      }

      groups.push(group)
    }

    return groups
  }

  async destroy(id: string | Object) {
    this.host.requireUser()
    id = this._resolveModelId(id)

    const operationResult = await this.host.api.groups.destroy(id)

    if (!operationResult) {
      throw new errors.NotFoundError(this.host.models.Group.name, id)
    }
  }

  @reusePromise()
  async findAllMemberIds(id: string | Object) {
    this.host.requireUser()
    id = this._resolveModelId(id)
    return this.host.api.groups.findAllMemberIds(id)
  }

  async addMembers(id: string | Object, memberIds: string | string[] | Object[]) {
    this.host.requireUser()
    id = this._resolveModelId(id)
    memberIds = arrayWrap(memberIds)
      .map(this._resolveModelId)
      .map(this.host.roles.__resolveRoleId)
    const group = this.getById(id)

    if (group && group.groupType === GroupType.ROLE_P2P) {
      throw new errors.PermissionDeniedError('role p2p group', group.id, 'add users')
    }

    if (group) {
      if (group.groupType === 'FORUM' && !group.memberIds) {
        group.memberIds = await this.findAllMemberIds(id)
      }
      memberIds = _.difference(memberIds, group.memberIds)
    }

    if (memberIds.length === 0) return

    const newGroup = await this.host.api.groups.addMembers(id, memberIds)

    if (this.config.condensedReplays) {
      let conversation = this.host.conversations.getById(newGroup.conversation_id)
      if (!conversation) {
        let rawConversation = await this.host.api.conversations.find(newGroup.conversation_id)

        if (!rawConversation) throw new errors.NotFoundError(this.host.models.Conversation.name, id)

        conversation = this.host.conversations.__injectConversation(rawConversation)
      }

      if (newGroup.unread_message_count) {
        conversation.unreadCount = newGroup.unread_message_count
      }

      if (newGroup.unread_priority_message_count) {
        conversation.unreadPriorityCount = newGroup.unread_priority_message_count
      }

      this.host.models.Conversation.inject(conversation)
    }
  }

  async removeMembers(id: string | Object, memberIds: string | string[] | Object[]) {
    this.host.requireUser()
    id = this._resolveModelId(id)
    memberIds = arrayWrap(memberIds)
      .map(this._resolveModelId)
      .map(this.host.roles.__resolveRoleId)
    const group = this.getById(id)

    if (group) {
      const { groupType, id: groupId, p2pSender } = group
      const botUserId = p2pSender && p2pSender.$entityType === 'role' && p2pSender.botUserId

      if (groupType === GroupType.ROLE_P2P && botUserId && memberIds.includes(botUserId)) {
        throw new errors.PermissionDeniedError('role p2p group', groupId, 'remove an on-duty user')
      }

      if (groupType === 'FORUM' && !group.memberIds) {
        group.memberIds = await this.findAllMemberIds(id)
      }
      memberIds = _.intersection(memberIds, group.memberIds)
    }

    if (memberIds.length === 0) return

    // removeMembers doesn't remove members from local DB
    // it will wait for a bang event to do so

    await this.host.api.groups.removeMembers(id, memberIds)
  }

  async addMember(id: string | Object, memberId: string | Object) {
    return this.addMembers(id, [memberId])
  }

  async removeMember(id: string | Object, memberId: string | Object) {
    return this.removeMembers(id, [memberId])
  }

  // TODO TEST
  async leave(id: string | Object) {
    id = this._resolveModelId(id)
    const group = this.getById(id)
    if (!group) return

    const { currentUser, currentUserId } = this.host
    const { groupType, p2pSender } = group
    let { memberIds } = group
    const myRoleBotIds = currentUser.roles.map(({ botUserId }) => botUserId)
    let leavingAs = [currentUserId]

    if (groupType === GroupType.FORUM) {
      memberIds = await this.findAllMemberIds(id)
    }

    if (groupType === GroupType.ROLE_P2P && p2pSender) {
      leavingAs = [p2pSender.$entityType === 'user' ? p2pSender.id : p2pSender.botUserId]
    } else if (memberIds) {
      for (const memberId of memberIds) {
        if (myRoleBotIds.includes(memberId)) {
          leavingAs.push(memberId)
        }
      }
    }

    await this.removeMembers(id, leavingAs)
  }

  reactToBangEvent({ data }) {
    data = jsonCloneDeep(data)
    const eventId = data['bang_id'] || `${data['sort_number']}`

    return this.__handleBang({ data, eventId, alwaysAddToTimeline: false })
  }

  __handleBang({ data, eventId, alwaysAddToTimeline = false }) {
    const action = data['event']

    if (action === 'added') {
      this.__addedToGroup({ data, eventId, alwaysAddToTimeline })
    } else if (action === 'removed') {
      this.__removedFromGroup({ data, eventId, alwaysAddToTimeline })
    } else if (EscalationChangeAction.fromServer(action)) {
      this.__handleEscalationBang({ data, eventId, alwaysAddToTimeline })
    }
  }

  __handleEscalationBang({ data, eventId, alwaysAddToTimeline }) {
    const callerEntity = data['caller_entity']
    const subjectEntity = data['subject_entity']
    const targetEntity = data['target_entity']
    const organizationId = data['organization_id']
    if (!subjectEntity['token'] || !targetEntity['token']) return

    let group = this.host.models.Group.get(targetEntity['token'])
    if (!group) {
      group = this.host.models.Group.injectPlaceholder({
        id: targetEntity['token'],
        organizationId,
      })
    }

    let escalationExecution = this.host.models.EscalationExecution.get(subjectEntity['token'])
    if (!escalationExecution) escalationExecution = group.escalationExecution
    if (!escalationExecution) {
      if (action === EscalationChangeAction.MEMBER_ADDED) {
        escalationExecution = null
      } else {
        escalationExecution = this.host.models.EscalationExecution.inject({
          id: subjectEntity['token'],
          organizationId,
        })
      }
    }

    const action = EscalationChangeAction.resolve(data['event'])
    const actionUserName = data['action_user_name']
    const actionTime = data['action_time'] || data['created_on']
    let actor = null, members = null

    if (action === EscalationChangeAction.INITIATED) {
      actor = {
        displayName: callerEntity['display_name'],
        id: callerEntity['token'],
        organizationId,
        type: 'user',
      }
    } else if (action === EscalationChangeAction.MEMBER_ADDED) {
      members = [{
        displayName: data['member_name'],
        id: data['member_id'],
        organizationId,
        type: 'user',
      }]
    } else if (action !== EscalationChangeAction.NO_RESPONSE) {
      actor = {
        displayName: actionUserName,
        id: null,
        organizationId,
        type: 'user',
      }
    }

    const newEventData = {
      action,
      actionTime,
      actor,
      alwaysAddToTimeline,
      createdAt: data['created_on'],
      escalationExecution,
      group,
      members,
      id: eventId,
      sortNumber: +data['sort_number'],
    }

    this.host.conversations.addEscalationExecutionChangeLog(newEventData)
  }

  __addedToGroup({ data, eventId, alwaysAddToTimeline }) {
    const callerEntity = data['caller_entity']
    const subjectEntity = data['subject_entity']
    const targetEntity = data['target_entity']
    const organizationId = data['organization_id']

    // group creator, member or group were deleted -- just ignore
    if (
      !callerEntity || !callerEntity['token']
      || !targetEntity || !targetEntity['token']
      || !subjectEntity || !subjectEntity['token']
    ) return

    const actor = {
      displayName: callerEntity['display_name'],
      id: callerEntity['token'],
      organizationId,
      type: 'user',
    }
    const memberToAdd = {
      displayName: subjectEntity['display_name'],
      id: subjectEntity['token'],
      organizationId,
      type: 'user',
    }

    // this event relies on the existence of the group in the local db
    const group = this.host.models.Group.get(targetEntity['token'])
    if (!group) return

    const actorModel = this.host.models.User.get(actor.id)
    const action = actorModel && actorModel.isRoleBot ? 'OPT_IN' :
      memberToAdd.id === actor.id ? 'JOIN' : 'ADD'
    const actionTime = data['action_time'] || data['created_on']

    const newEventData = {
      action,
      actionTime,
      actor,
      alwaysAddToTimeline,
      createdAt: data['created_on'],
      group,
      id: eventId,
      members: [memberToAdd],
      memberIds: [memberToAdd.id],
      sortNumber: +data['sort_number'],
    }

    // add a change log anyway, but fire membership:change only if there was an actual change
    this.host.conversations.addMembershipChangeLog(newEventData)

    if (action === 'OPT_IN' || !group.memberIds || !group.memberIds.includes(memberToAdd.id)) {
      this.host.models.Group.touch(group)
      this.emit('membership:change', newEventData)
    }
  }

  __removedFromGroup({ data, eventId, alwaysAddToTimeline }) {
    const callerEntity = data['caller_entity']
    const subjectEntity = data['subject_entity']
    const targetEntity = data['target_entity']
    const organizationId = data['organization_id']

    // group creator, member or group were deleted -- just ignore
    if (
      !callerEntity || !callerEntity['token']
      || !targetEntity || !targetEntity['token']
      || !subjectEntity || !subjectEntity['token']
    ) return

    const group = this.host.models.Group.get(targetEntity['token'])
    if (!group) return

    const actor = {
      displayName: callerEntity['display_name'],
      id: callerEntity['token'],
      organizationId,
      type: 'user',
    }
    const memberToRemove = {
      displayName: subjectEntity['display_name'],
      id: subjectEntity['token'],
      organizationId,
      type: 'user',
    }

    const actorModel = this.host.models.User.get(actor.id)
    const action = actorModel && actorModel.isRoleBot ? 'OPT_OUT' :
      memberToRemove.id === actor.id ? 'LEAVE' : 'REMOVE'
    const actionTime = data['action_time'] || data['created_on']

    const newEventData = {
      action,
      actionTime,
      actor,
      alwaysAddToTimeline,
      createdAt: data['created_on'],
      group,
      id: eventId,
      members: [memberToRemove],
      memberIds: [memberToRemove.id],
      sortNumber: +data['sort_number'],
    }

    // add a change log anyway, but fire membership:change only if there was an actual change
    this.host.conversations.addMembershipChangeLog(newEventData)

    if (action === 'OPT_OUT' || !group.memberIds || group.memberIds.includes(memberToRemove.id)) {
      this.emit('membership:change', newEventData)
    }
  }

  _setP2PEntities = (group) => {
    const { createdByRole, createdByUser, groupType, targetUser, targetRole } = group
    const { currentUser } = this.host
    if (!currentUser || groupType !== GroupType.ROLE_P2P) return

    const { roles: myRoles } = currentUser
    const otherUsers = []
    const roles = []

    if (createdByRole) {
      roles.push(createdByRole)
    } else if (createdByUser && createdByUser !== currentUser) {
      otherUsers.push(createdByUser)
    }
    if (targetRole) {
      roles.push(targetRole)
    } else if (targetUser && targetUser !== currentUser) {
      otherUsers.push(targetUser)
    }

    let p2pRecipient = null
    let p2pSender = null
    if (roles.length === 1) {
      if (createdByUser === currentUser || targetUser === currentUser) {
        if (myRoles.includes(roles[0])) {
          p2pRecipient = currentUser
          p2pSender = roles[0]
        } else {
          p2pRecipient = roles[0]
          p2pSender = currentUser
        }
      } else {
        p2pRecipient = otherUsers[0]
        p2pSender = roles[0]
      }
    } else if (roles.length === 2) {
      if (myRoles.includes(roles[0])) {
        p2pRecipient = roles[1]
        p2pSender = roles[0]
      } else if (myRoles.includes(roles[1])) {
        p2pRecipient = roles[0]
        p2pSender = roles[1]
      }
    }

    group.p2pRecipient = p2pRecipient
    group.p2pSender = p2pSender
  }

  _onChangeConversation = (resource, conversation) => {
    const { counterPartyId, counterPartyType } = conversation
    if (counterPartyType !== 'group') return

    const group = this.host.models.Group.get(counterPartyId)
    if (!group) return

    if (!group.conversation) {
      this.host.models.Group.inject(group)
    }
  }

  _onRemoveConversation = (resource, conversation) => {
    const { counterPartyId, counterPartyType } = conversation
    if (counterPartyType !== 'group') return

    const group = this.host.models.Group.get(counterPartyId)
    if (!group) return

    if (group.conversation) {
      this.host.models.Group.inject(group)
    }
  }

  _onChangeGroup = (resource, group) => {
    const { id: groupId, memberIds } = group
    const lastGroupMemberIds = this._groupMemberIds[groupId]

    if (lastGroupMemberIds) {
      for (const userId of lastGroupMemberIds) {
        const groups = this._userGroups[userId]
        const idx = groups ? groups.indexOf(group) : -1
        if (idx > -1 && memberIds && !memberIds.includes(userId)) {
          groups.splice(idx, 1)
        }
      }
    }

    if (!memberIds) return
    this._groupMemberIds[groupId] = memberIds

    for (const userId of memberIds) {
      const groups = this._userGroups[userId] || []
      if (!groups.includes(group)) {
        groups.push(group)
      }
      this._userGroups[userId] = groups
    }
  }

  _onRemoveGroup = (resource, group) => {
    const { id: groupId, memberIds } = group
    delete this._groupMemberIds[groupId]
    if (!memberIds) return

    for (const userId of memberIds) {
      const groups = this._userGroups[userId]
      const idx = groups ? groups.indexOf(group) : -1
      if (idx > -1) {
        groups.splice(idx, 1)
      }
    }
  }

  _onChangeMetadata = (resource, metadata) => {
    const { entityId } = metadata
    const group = this.host.models.Group.get(entityId)
    if (!group) return

    this.host.models.Group.inject(group)
  }

  _onChangeUser = (resource, user) => {
    const { botRole, id: userId, isRoleBot } = user
    const groups = this._userGroups[userId]
    const prevUserState = this._userState[userId]
    const changedRoleState = prevUserState && (
      prevUserState.botRole !== botRole ||
      prevUserState.isRoleBot !== isRoleBot
    )

    if (!prevUserState || changedRoleState) {
      this._userState[userId] = { botRole, isRoleBot }
    }

    if (groups) {
      for (const group of groups) {
        const { conversation, groupType, members } = group

        if (
          groupType === GroupType.ROLE_P2P ||
          changedRoleState ||
          !members || !members.includes(user)
        ) {
          this.host.models.Group.inject(group)
          if (conversation) {
            this.host.models.Conversation.inject(conversation)
          }
        }
      }
    }
  }

  _onRemoveUser = (resource, user) => {
    const { id: userId } = user
    const groups = this._userGroups[userId]

    if (groups && groups.length > 0) {
      for (const group of groups) {
        const { members } = group
        if (members && members.includes(user)) {
          this.host.models.Group.inject(group)
        }
      }
    }

    delete this._userGroups[userId]
    delete this._userState[userId]
  }
}
